import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

import toast from 'react-hot-toast';

const SegmentsTab = ({ segments, orgId, onSegmentsUpdated }) => {
  const { organization } = useAuth();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', filters: [] });
  const [saving, setSaving] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);

  const serviceTypes = organization?.settings?.service_types || [];

  const handleAddFilter = (type, value) => {
    setForm((prev) => ({ ...prev, filters: [...prev?.filters, { type, value }] }));
  };

  const handleRemoveFilter = (idx) => {
    setForm((prev) => ({ ...prev, filters: prev?.filters?.filter((_, i) => i !== idx) }));
  };

  const handlePreview = async () => {
    const { count } = await supabase?.from('contacts')?.select('id', { count: 'exact', head: true })?.eq('org_id', orgId);
    setPreviewCount(count || 0);
  };

  const handleSave = async () => {
    if (!form?.name?.trim()) { toast?.error('Segment name required'); return; }
    setSaving(true);
    const { error } = await supabase?.from('segments')?.insert({
      org_id: orgId,
      name: form?.name,
      description: form?.description,
      filters: form?.filters,
      contact_count: previewCount || 0,
    });
    setSaving(false);
    if (!error) {
      toast?.success('Segment created');
      onSegmentsUpdated?.();
      setShowCreate(false);
      setForm({ name: '', description: '', filters: [] });
      setPreviewCount(null);
    } else {
      toast?.error('Failed to create segment');
    }
  };

  const handleDelete = async (id) => {
    await supabase?.from('segments')?.delete()?.eq('id', id);
    onSegmentsUpdated?.();
    toast?.success('Segment deleted');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-foreground">Audience Segments</h2>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center space-x-1.5 px-3 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors">
          <Icon name="Plus" size={16} />
          <span>Create Segment</span>
        </button>
      </div>
      {/* Segment Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {segments?.length === 0 ? (
          <div className="col-span-2 flex flex-col items-center justify-center py-12 text-center">
            <Icon name="Users" size={28} className="text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No segments yet. Create your first audience segment.</p>
          </div>
        ) : (
          segments?.map((seg) => (
            <div key={seg?.id} className="bg-background border border-border rounded-xl p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">{seg?.name}</h3>
                  {seg?.description && <p className="text-xs text-muted-foreground mt-0.5">{seg?.description}</p>}
                </div>
                <button onClick={() => handleDelete(seg?.id)} className="text-muted-foreground hover:text-red-500 transition-colors">
                  <Icon name="Trash2" size={14} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1">
                  <Icon name="Users" size={14} className="text-muted-foreground" />
                  <span className="text-sm font-semibold text-foreground">{seg?.contact_count || 0}</span>
                  <span className="text-xs text-muted-foreground">contacts</span>
                </div>
                {Array.isArray(seg?.filters) && seg?.filters?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {seg?.filters?.slice(0, 2)?.map((f, i) => (
                      <span key={i} className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                        {f?.type}: {f?.value}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreate(false)} />
          <div className="relative bg-background border border-border rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-base font-semibold text-foreground">Create Segment</h3>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Segment Name *</label>
                <input type="text" value={form?.name} onChange={(e) => setForm((p) => ({ ...p, name: e?.target?.value }))}
                  placeholder="e.g. Active Leads"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
                <input type="text" value={form?.description} onChange={(e) => setForm((p) => ({ ...p, description: e?.target?.value }))}
                  placeholder="Optional description"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>

              {/* Filters */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Filters</label>
                <div className="space-y-2 mb-2">
                  {form?.filters?.map((f, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-muted rounded-lg">
                      <span className="text-xs text-foreground">{f?.type}: {f?.value}</span>
                      <button onClick={() => handleRemoveFilter(i)} className="text-muted-foreground hover:text-red-500">
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {serviceTypes?.length > 0 && (
                    <select onChange={(e) => e?.target?.value && handleAddFilter('service_type', e?.target?.value)}
                      className="px-2 py-1.5 border border-border rounded-lg bg-background text-xs focus:outline-none">
                      <option value="">+ Service Type</option>
                      {serviceTypes?.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  )}
                  <select onChange={(e) => e?.target?.value && handleAddFilter('lead_status', e?.target?.value)}
                    className="px-2 py-1.5 border border-border rounded-lg bg-background text-xs focus:outline-none">
                    <option value="">+ Lead Status</option>
                    {['new', 'contacted', 'qualified', 'converted']?.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select onChange={(e) => e?.target?.value && handleAddFilter('job_status', e?.target?.value)}
                    className="px-2 py-1.5 border border-border rounded-lg bg-background text-xs focus:outline-none">
                    <option value="">+ Job Status</option>
                    {['scheduled', 'in_progress', 'completed']?.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* Preview */}
              <div className="flex items-center space-x-3">
                <button onClick={handlePreview}
                  className="px-3 py-1.5 border border-border rounded-lg text-xs font-medium hover:bg-muted transition-colors">
                  Preview Count
                </button>
                {previewCount !== null && (
                  <span className="text-sm font-semibold text-foreground">{previewCount} contacts</span>
                )}
              </div>
            </div>
            <div className="flex space-x-3 p-4 border-t border-border">
              <button onClick={() => setShowCreate(false)} className="flex-1 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
                {saving ? 'Saving...' : 'Create Segment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SegmentsTab;
